import { useEffect } from 'react';

/**
 * Custom hook for keyboard shortcuts
 * @param {Object} shortcuts - Object mapping key combinations to handlers
 * @param {boolean} enabled - Whether shortcuts are enabled (default: true)
 * @param {Array} dependencies - Dependencies array for the effect
 */
export const useKeyboardShortcuts = (shortcuts, enabled = true, dependencies = []) => {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event) => {
      // Don't trigger shortcuts when typing in inputs, textareas, or contenteditable elements
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable ||
        event.target.closest('[contenteditable="true"]')
      ) {
        return;
      }

      // Build key string (e.g., "ctrl+a", "escape", "a")
      const keyParts = [];
      if (event.ctrlKey || event.metaKey) {
        keyParts.push(event.ctrlKey ? 'ctrl' : 'cmd');
      }
      if (event.shiftKey) {
        keyParts.push('shift');
      }
      if (event.altKey) {
        keyParts.push('alt');
      }
      
      // Add the main key (normalize to lowercase)
      const mainKey = event.key.toLowerCase();
      keyParts.push(mainKey);
      
      const keyString = keyParts.join('+');
      
      // Check if this key combination is registered
      if (shortcuts[keyString]) {
        event.preventDefault();
        event.stopPropagation();
        shortcuts[keyString](event);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...dependencies]);
};

